import type { DatasetOptionUi } from "../types/documentManagementUiTypes";

type MetadataFieldKey = "company_name" | "product_name" | "model_name";

export function metadataFieldLabel(
  key: MetadataFieldKey,
  t: (key: string, options?: { defaultValue?: string }) => string
): string {
  switch (key) {
    case "company_name":
      return t("opva.dmColCompany", { defaultValue: "Company" });
    case "product_name":
      return t("opva.dmColProduct", { defaultValue: "Product" });
    case "model_name":
      return t("opva.dmColModel", { defaultValue: "Model" });
  }
}

export function parseRoleAccess(raw: string | string[] | undefined): string[] {
  if (!raw) return ["*"];
  if (Array.isArray(raw)) return raw;
  try {
    const v = JSON.parse(raw) as unknown;
    return Array.isArray(v) ? (v as string[]) : ["*"];
  } catch {
    return ["*"];
  }
}

/** Collapse a full explicit role list to `["*"]` for API payloads. */
export function normalizeRoleAccessForSave(
  roleAccess: string[],
  roleOptions: { name: string }[]
): string[] {
  if (!roleAccess.length) {
    return [];
  }
  if (roleAccess.includes("*")) {
    return ["*"];
  }
  const trimmed = roleAccess.filter((name) => Boolean(name?.trim()));
  const allRoleNames = roleOptions.map((r) => r.name);
  if (
    allRoleNames.length > 0 &&
    allRoleNames.every((name) => trimmed.includes(name))
  ) {
    return ["*"];
  }
  return trimmed;
}

export function roleAccessLabel(raw: string | string[] | undefined): string {
  const ra = parseRoleAccess(raw);
  if (!ra.length || ra.includes("*")) return "All";
  return ra.join(", ");
}

/** Compact table label: "Super Admin +5" (first role + remaining count). */
export function roleAccessCompactDisplayLabel(
  raw: string | string[] | undefined,
  t: (key: string, options?: { defaultValue?: string; count?: number; first?: string }) => string
): string {
  const ra = parseRoleAccess(raw);
  if (!ra.length || ra.includes("*")) {
    return t("opva.dmAllRolesShort", { defaultValue: "All" });
  }
  if (ra.length === 1) {
    return ra[0]!;
  }
  return t("opva.dmRolesCompact", {
    defaultValue: "{{first}} +{{count}}",
    first: ra[0]!,
    count: ra.length - 1,
  });
}

export function findDatasetById(
  id: number,
  datasets: DatasetOptionUi[]
): DatasetOptionUi | undefined {
  return datasets.find((d) => d.id === id);
}

export function resolveUploadDatasetTarget(
  mode: "existing" | "new",
  selection: number | "",
  newName: string,
  newDescription: string,
  datasets: DatasetOptionUi[]
):
  | { kind: "existing"; dataset: DatasetOptionUi }
  | { kind: "new"; name: string; description: string }
  | { errorKey: string; defaultMessage: string } {
  if (mode === "new") {
    const trimmed = newName.trim();
    if (!trimmed) {
      return {
        errorKey: "opva.dmNewDatasetNameRequired",
        defaultMessage: "Enter a name for the new dataset.",
      };
    }
    return {
      kind: "new",
      name: trimmed,
      description: newDescription.trim(),
    };
  }
  if (selection === "" || selection == null) {
    return {
      errorKey: "opva.dmDatasetRequired",
      defaultMessage: "Select a dataset before uploading.",
    };
  }
  const existing = findDatasetById(selection, datasets);
  if (!existing) {
    return {
      errorKey: "opva.dmDatasetRequired",
      defaultMessage: "Select a dataset before uploading.",
    };
  }
  return { kind: "existing", dataset: existing };
}
